import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FreddyMark, FreddyWordmark } from "./FreddyLogo";
import { useAuth } from "../lib/auth";
import { cn } from "../lib/utils";
import {
  LayoutDashboard,
  Store,
  Users,
  ShoppingCart,
  Bell,
  Settings,
  FileClock,
  LogOut,
  Menu,
  Gavel,
  Repeat,
  Wrench,
  AlertCircle,
  Award,
  Chrome,
  Upload,
} from "lucide-react";

const CORPORATE_NAV = [
  { to: "/corporate", icon: LayoutDashboard, label: "Overview" },
  { to: "/corporate/rooftops", icon: Store, label: "Rooftops" },
  { to: "/corporate/buyers", icon: Users, label: "Buyers" },
  { to: "/corporate/acquisitions", icon: ShoppingCart, label: "Acquisitions" },
  { to: "/corporate/alerts", icon: Bell, label: "Alerts" },
  { to: "/corporate/audit-log", icon: FileClock, label: "Audit log" },
  { to: "/corporate/settings", icon: Settings, label: "Settings" },
];

const ROOFTOP_NAV = (rid) => [
  { to: `/rooftop/${rid}`, icon: LayoutDashboard, label: "Today's buy list" },
  { to: `/rooftop/${rid}/live-auctions`, icon: Gavel, label: "Live auctions" },
  { to: `/rooftop/${rid}/trade-queue`, icon: Repeat, label: "Trade queue" },
  { to: `/rooftop/${rid}/service-drive`, icon: Wrench, label: "Service drive" },
  { to: `/rooftop/${rid}/stock-pressure`, icon: AlertCircle, label: "Stock pressure" },
  { to: `/rooftop/${rid}/performance`, icon: Award, label: "Performance" },
];

const SHARED_NAV = [
  { to: "/extension-preview", icon: Chrome, label: "Extension preview" },
  { to: "/upload", icon: Upload, label: "Upload vAuto" },
];

export function Layout({ children, variant = "corporate", rooftopId, rooftopName }) {
  const [expanded, setExpanded] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    if (window.innerWidth < 1024) setExpanded(false);
  }, []);

  const primary =
    variant === "rooftop" && rooftopId
      ? ROOFTOP_NAV(rooftopId)
      : CORPORATE_NAV;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={cn(
          "sidebar-transition grain relative flex flex-col bg-navy text-white overflow-hidden shrink-0",
          expanded ? "w-[220px]" : "w-[64px]",
        )}
      >
        <div className="relative z-10 flex flex-col h-full">
          <div className="flex items-center gap-2 px-3 h-16 border-b border-white/10">
            <button
              data-testid="sidebar-toggle"
              onClick={() => setExpanded((e) => !e)}
              className="p-1.5 hover:bg-white/10 rounded-md transition-colors"
              aria-label="toggle sidebar"
            >
              <Menu size={18} className="text-white" />
            </button>
            {expanded && (
              <FreddyWordmark variant="white" height={26} showTagline={false} />
            )}
          </div>

          <nav className="flex-1 py-4 space-y-0.5">
            {primary.map((item) => {
              const active =
                location.pathname === item.to ||
                (item.to !== "/corporate" && location.pathname.startsWith(item.to));
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={cn(
                    "group flex items-center gap-3 h-10 px-3 mx-2 rounded-md transition-colors text-sm font-medium",
                    active
                      ? "bg-freddy-red text-white"
                      : "text-white/70 hover:text-white hover:bg-white/5",
                  )}
                >
                  <item.icon size={16} className="shrink-0" />
                  {expanded && <span className="truncate">{item.label}</span>}
                </Link>
              );
            })}

            <div className="h-px bg-white/10 my-3 mx-4" />

            {SHARED_NAV.map((item) => {
              const active = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={cn(
                    "group flex items-center gap-3 h-10 px-3 mx-2 rounded-md transition-colors text-sm font-medium",
                    active
                      ? "bg-white/15 text-white"
                      : "text-white/60 hover:text-white hover:bg-white/5",
                  )}
                >
                  <item.icon size={16} className="shrink-0" />
                  {expanded && <span className="truncate">{item.label}</span>}
                </Link>
              );
            })}
          </nav>

          <div className="border-t border-white/10 p-3">
            {expanded ? (
              <div className="space-y-2">
                <div className="text-[11px] text-white/50 uppercase tracking-wider font-semibold">
                  Signed in
                </div>
                <div className="text-sm text-white font-semibold truncate">{user?.name}</div>
                <div className="text-[11px] text-white/60 capitalize truncate">
                  {user?.role?.replace("_", " ")}
                </div>
                <button
                  data-testid="logout-btn"
                  onClick={handleLogout}
                  className="mt-2 w-full flex items-center justify-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-white/80 hover:text-white bg-white/5 hover:bg-white/10 py-2 rounded-md"
                >
                  <LogOut size={12} /> Log out
                </button>
              </div>
            ) : (
              <button
                data-testid="logout-btn-collapsed"
                onClick={handleLogout}
                className="w-full flex items-center justify-center p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-md"
              >
                <LogOut size={16} />
              </button>
            )}
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 shrink-0">
          <div className="flex items-center gap-4">
            <FreddyWordmark />
            {variant === "rooftop" && rooftopName && (
              <div className="hidden md:flex items-center gap-2 pl-4 border-l border-slate-200">
                <Store size={14} className="text-slate-500" />
                <span className="text-sm font-semibold text-navy">{rooftopName}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:flex flex-col text-right">
              <div className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">
                Last sync
              </div>
              <div className="text-xs text-navy font-semibold tabular">
                {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} · live
              </div>
            </div>
            <div className="w-9 h-9 rounded-full bg-navy text-white flex items-center justify-center text-xs font-bold">
              {(user?.name || "U").split(" ").map((n) => n[0]).join("").slice(0, 2)}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-6 md:p-8">{children}</main>

        <footer className="border-t border-slate-200 bg-white px-6 py-3 flex items-center justify-between text-[11px] text-slate-500">
          <div className="flex items-center gap-2">
            <FreddyMark size={14} />
            <span className="font-semibold">Built by Freddy Media · Jacksonville FL</span>
          </div>
          <div>Freddy Buy Signal · v1.0</div>
        </footer>
      </div>
    </div>
  );
}
